<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal :visible="modal.show" :data="model"
                   :title="model.uuid ? $t(`Update Category`) : $t(`Create New Category`)" @show="onShow"
                   @hidden="onHidden" size="lg" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading"
                   scrollable>
      <translatable-input-widget :hint="false" :model="model" :fields="model.getTranslatableFieldsArray()"
                                 :errors="errors"/>
      <div class="row">
        <div class="col-md-3">
          <currency-input-widget :model="model" attribute="lot_handling_charge"/>
        </div>
        <div class="col-md-3">
          <percentage-input-widget :model="model" attribute="tax_rate"/>
        </div>
        <div class="col-md-3">
          <percentage-input-widget :model="model" attribute="insurance_rate"/>
        </div>
        <div class="col-md-3">
          <currency-input-widget :model="model" attribute="withdrawal_fee"/>
        </div>
      </div>
      <div class="row" v-if="inventoryLotAutoSelectEnable">
        <div class="col-md-6">
          <input-widget type="number" :model="model" attribute="lot_start_number" :rules="{regex: LOT_NO_REGEX}"/>
        </div>
        <div class="col-md-6">
          <input-widget type="number" :model="model" attribute="lot_end_number" :rules="{regex: LOT_NO_REGEX}"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card mb-3">
            <div class="card-header">
              {{ $t("Premiums") }}
            </div>
            <div class="card-body">
              <premium-tiers :model="model" enable-first-tier-removal show-minimum-premium-field/>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="card mb-3">
            <div class="card-header">
              {{ $t("Commissions") }}
            </div>
            <div class="card-body">
              <commission-tiers :model="model" enable-first-tier-removal showMinimumCommissionField/>
            </div>
          </div>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import CategoryModel from "./Category.model";
  import {createNamespacedHelpers} from "vuex";
  import {Formatter} from "@/core/services/formatter.service";
  import TranslatableInputWidget from "../../../core/widgets/TranslatableInputWidget";
  import PremiumTiers from "@/core/components/premium-tiers/PremiumTiers";
  import CommissionTiers from "@/core/components/commission-tiers/CommissionTiers";
  import Premium from "@/core/components/premium-tiers/Premium.model";
  import Commission from "@/core/components/commission-tiers/Commission.model";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import PercentageInputWidget from "@/core/widgets/PercentageInputWidget";
  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import {LOT_NO_REGEX} from "@/plugins/vee-validate";

  const {mapState, mapActions} = createNamespacedHelpers('setup/category');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "CategoryModal",
    components: {
      InputWidget,
      CurrencyInputWidget,
      PercentageInputWidget,
      CommissionTiers,
      PremiumTiers,
      TranslatableInputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new CategoryModel(),
        LOT_NO_REGEX: LOT_NO_REGEX
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object
      }),
      ...globalMapState({
        inventoryLotAutoSelectEnable: state => state.inventoryLotAutoSelectEnable
      })
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new CategoryModel(this.editObject);
          this.model.premiums = this.model.premiums.map(data => new Premium(data))
          this.model.commissions = this.model.commissions.map(data => new Commission(data))
        }
      }
    },
    methods: {
      ...mapActions(['hideModal', 'createCategory', 'updateCategory']),
      onShow() {
        this.showConfirmation = true
      },
      onHidden() {
        this.model = new CategoryModel();
        this.hideModal();
      },
      async onSubmit() {
        this.model.resetErrors();
        this.model.premiums.forEach(p => p.resetErrors())
        this.model.commissions.forEach(c => c.resetErrors())
        if (!this.model.premiums || this.model.premiums.length === 0) {
          this.model.minimum_premium = null;
        }

        if (!this.model.commissions || this.model.commissions.length === 0) {
          this.model.minimum_commission = null;
        }

        this.loading = true;
        let response;

        if (this.model.uuid) {
          response = await this.updateCategory(this.model);
        } else {
          response = await this.createCategory(this.model);
        }

        this.loading = false;

        if (response.success) {
          if (this.model.uuid) {
            this.$toast('success', this.$t('Category was successfully updated'));
          } else {
            this.$toast('success', this.$t('Category was successfully created'));
          }
          this.showConfirmation = false;
          this.hideModal();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            console.log(response.body);
            this.model.setErrors(response.body)
            if (response.body.premiums) {
              for (let index in response.body.premiums) {
                this.model.premiums[index].setMultipleErrors(response.body.premiums[index]);
              }
            }
            if (response.body.commissions) {
              for (let index in response.body.commissions) {
                this.model.commissions[index].setMultipleErrors(response.body.commissions[index]);
              }
            }
          }
        }
      },
      onNameChange(val) {
        this.model.key = Formatter.toSlug(val);
      }
    }
  }
</script>

<style scoped>

</style>
